/**
 * Herkent de fout die ontstaat wanneer een browser een pagina openhoudt terwijl
 * Collectr opnieuw wordt uitgerold.
 *
 * WAT ER GEBEURT. De openstaande pagina verwijst naar chunknamen met een hash
 * die na de nieuwe build niet meer bestaat. Cloudflare Pages beantwoordt zo'n
 * gemist bestand NIET met 404 maar met index.html (HTTP 200, text/html) vanwege
 * de SPA-fallback — gemeten op productie: `/assets/bestaat-niet-xyz.js` levert
 * 200 met content-type text/html. De browser weigert HTML als module te laden en
 * meldt dat als "Failed to fetch dynamically imported module".
 *
 * WAAROM DIT APART STAAT. Het is géén programmeerfout maar een verouderde
 * pagina, en het verdient dus een ander antwoord (herladen) én een andere
 * behandeling in de foutenrapportage (niet melden — het hoort bij elke deploy en
 * zou de telling vervuilen). Elke browser formuleert het anders, vandaar de
 * meerdere varianten.
 */
function berichtVan(error: unknown): string {
  if (error instanceof Error) return `${error.name} ${error.message}`;
  return typeof error === "string" ? error : "";
}

/** De vier meldingen die een browser geeft wanneer een module niet te laden is. */
function isLaadfoutmelding(genormaliseerd: string): boolean {
  return (
    genormaliseerd.includes("failed to fetch dynamically imported module") ||
    genormaliseerd.includes("error loading dynamically imported module") ||
    genormaliseerd.includes("importing a module script failed") ||
    genormaliseerd.includes("expected a javascript module script")
  );
}

/**
 * De GEVOLGFOUT van een mislukte chunk, per browser anders geformuleerd.
 *
 * Waarom dit een aparte categorie is: `React.lazy` leest `.default` van wat de
 * import opleverde. Kwam daar niets uit, dan is de fout die de gebruiker bereikt
 * niet de laadfout zelf maar een TypeError op `undefined`. Op productie zag de
 * founder precies dat, en de herkenning hierboven zag hem niet — dus kreeg hij
 * "Er is iets misgegaan" én een Sentry-alarm voor iets dat bij elke deploy hoort.
 */
function isGevolgVanOntbrekendeModule(genormaliseerd: string): boolean {
  return (
    genormaliseerd.includes("reading 'default'") ||
    genormaliseerd.includes('reading "default"') ||
    genormaliseerd.includes("evaluating 'e.default'") ||
    /can't access property "default"/.test(genormaliseerd) ||
    /undefined is not an object.*\.default/.test(genormaliseerd)
  );
}

/**
 * `sessionStorage`-vorm, injecteerbaar zodat de teller getoetst kan worden
 * zonder browser. Bewust minimaal: meer dan dit heeft deze logica niet nodig.
 */
export interface HerlaadOpslag {
  getItem(sleutel: string): string | null;
  setItem(sleutel: string, waarde: string): void;
  removeItem(sleutel: string): void;
}

export const HERLAAD_TELLER = "collectr:chunk-herladen";
export const PRELOAD_FOUT_GEZIEN = "collectr:chunk-fout";

/**
 * Hoogstens twee herlaadpogingen. Eén is te krap wanneer de eerste herlading
 * zelf nog een verouderd document uit de cache krijgt; drie is een lus.
 */
export const MAX_HERLADINGEN = 2;

function telling(opslag: HerlaadOpslag): number {
  const rauw = Number.parseInt(opslag.getItem(HERLAAD_TELLER) ?? "0", 10);
  return Number.isFinite(rauw) && rauw > 0 ? rauw : 0;
}

/** Vastleggen dat er in deze sessie een chunk niet geladen kon worden. */
export function noteerPreloadFout(opslag: HerlaadOpslag): void {
  opslag.setItem(PRELOAD_FOUT_GEZIEN, "1");
}

export function preloadFoutGezien(opslag: HerlaadOpslag): boolean {
  return opslag.getItem(PRELOAD_FOUT_GEZIEN) === "1";
}

/**
 * Mag er nog een herlaadpoging? Verhoogt de teller wanneer het antwoord ja is.
 *
 * DE FOUT DIE HIER ZAT, EN DIE DE FOUNDER OP 05-08-2026 TROF. De teller was een
 * vlag die bij het `load`-event weer gewist werd, "want dan is het gelukt". Maar
 * `load` vuurt bij ELKE documentlading — óók bij de herlading die de handler net
 * zelf veroorzaakte. De vlag overleefde dus nooit een reload, precies de
 * situatie waarvoor hij bedoeld was, en de pagina bleef verversen. Alleen een
 * WERKELIJK geladen chunk mag de teller wissen; zie `noteerChunkGeladen`.
 */
export function magNogHerladen(opslag: HerlaadOpslag, max: number = MAX_HERLADINGEN): boolean {
  const gedaan = telling(opslag);
  if (gedaan >= max) return false;
  opslag.setItem(HERLAAD_TELLER, String(gedaan + 1));
  return true;
}

/** Het enige bewijs dat telt: er is werkelijk een chunk binnengekomen. */
export function noteerChunkGeladen(opslag: HerlaadOpslag): void {
  opslag.removeItem(HERLAAD_TELLER);
  opslag.removeItem(PRELOAD_FOUT_GEZIEN);
}

/**
 * Is dit een verouderde pagina in plaats van een programmeerfout?
 *
 * De gevolgfout (`reading 'default'`) telt UITSLUITEND mee wanneer er in deze
 * sessie ook werkelijk een chunk is mislukt. Zonder die voorwaarde zou elke
 * `TypeError` op een ontbrekende `default` als "herlaad maar even" worden
 * afgedaan en nooit in Sentry belanden — dan ruilt de ene blinde vlek voor de
 * andere.
 */
export function isStaleDeploymentError(
  error: unknown,
  opslag: HerlaadOpslag | null = null,
): boolean {
  const genormaliseerd = berichtVan(error).toLowerCase();
  if (isLaadfoutmelding(genormaliseerd)) return true;
  if (opslag === null || !preloadFoutGezien(opslag)) return false;
  return isGevolgVanOntbrekendeModule(genormaliseerd);
}
